import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useStagger } from '../hooks/useInView';
import Footer from '../components/sections/Footer.js';
import CalendlyLink from '../components/global/CalendlyLink';
import TrackedLink from '../components/global/TrackedLink';
import { CTA_IDS, CTA_LOCATIONS } from '../lib/analytics.constants';

const servicios = [
  {
    id: 'ia',
    title: 'Inteligencia Artificial',
    subtitle: 'IA integrada al negocio real, no un bot genérico',
    items: [
      'Asistentes conectados a tus procesos y datos',
      'Automatización de documentos y back-office',
      'Integración con WhatsApp, CRM y sistemas',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    highlight: true,
    link: '/ia',
    linkText: 'Saber más sobre IA',
  },
  {
    id: 'web',
    title: 'Desarrollo Web',
    subtitle: 'Presencia digital que genera negocio',
    items: [
      'Sitios corporativos, landing pages y portales',
      'Aplicaciones web progresivas (PWA)',
      'Paneles de usuario y plataformas interactivas',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
  },
  {
    id: 'app',
    title: 'Desarrollo App',
    subtitle: 'Tu producto en el bolsillo de tus usuarios',
    items: [
      'Apps nativas iOS y Android',
      'React Native y Flutter cross-platform',
      'Push notifications, pagos y geolocalización',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'medida',
    title: 'Desarrollo a Medida',
    subtitle: 'Sistemas diseñados para tu operación exacta',
    items: [
      'CRMs, ERPs y sistemas de gestión internos',
      'Integraciones entre herramientas existentes',
      'Automatización de procesos repetitivos',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    id: 'plataformas',
    title: 'Desarrollo de Plataformas',
    subtitle: 'Ecosistemas digitales listos para escalar',
    items: [
      'Marketplaces y plataformas multi-tenant',
      'Flujos de pagos, suscripciones y facturación',
      'APIs y arquitecturas orientadas a escala',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12H3m18 0h-2M12 5V3m0 18v-2m4.243-13.243l-1.415 1.415M6.172 17.828l-1.415 1.415m12.072 0l-1.415-1.415M6.172 6.172L4.757 4.757M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'saas',
    title: 'SaaS & MVP',
    subtitle: 'De la idea al producto validado, rápido',
    items: [
      'Prototipo funcional en semanas',
      'Arquitectura lista para crecer',
      'Iteración basada en feedback real',
    ],
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

const Services = ({ footerRef }) => {
  const { ref: heroHeadRef, itemStyle: heroStyle } = useStagger({ count: 4, stagger: 70 });
  const { ref: gridHeadRef, itemStyle: gridHeadStyle } = useStagger({ count: 2, stagger: 70 });
  const { ref: processHeadRef, itemStyle: processHeadStyle } = useStagger({ count: 2, stagger: 70 });
  const { ref: servicesRef, itemStyle } = useStagger({ count: 6, stagger: 55 });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <Helmet>
        <title>Desarrollo de Software a Medida en Paraguay · Apps, SaaS y Sistemas | eien</title>
        <meta name="description" content="Desarrollo de software a medida en Paraguay: aplicaciones web, apps móviles, SaaS, sistemas de gestión empresarial y automatización de procesos para empresas y startups en Asunción y LATAM." />
        <link rel="canonical" href="https://eientech.io/capacidades" />
      </Helmet>

      <div className="bg-gray-50 dark:bg-[#0a0a0a] min-h-screen">

        {/* Hero */}
        <section className="pt-24 pb-16 px-4 md:px-6">
          <div ref={heroHeadRef} className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center">
              <p style={heroStyle(0)} className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Capacidades</p>
            </div>
            <h1 style={heroStyle(1)} className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-tight mb-6 text-[#0b2b40] dark:text-white">
              Software a medida que{' '}
              <span className="text-gradient text-short">impulsa tu negocio</span>
            </h1>
            <p style={heroStyle(2)} className="text-xl text-slate-500 dark:text-slate-400 font-light leading-relaxed mb-8">
              No te vendemos tareas — te resolvemos problemas reales.
              Construimos lo que necesitás, con el modelo que mejor se adapta a vos.
            </p>
            <div style={heroStyle(3)} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <CalendlyLink
                ctaId={CTA_IDS.CAPACIDADES_HERO_CALENDLY}
                ctaLocation={CTA_LOCATIONS.HERO}
                className="inline-flex items-center gap-2 bg-[#11b36d] text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
              >
                Armar mi plan en 48 h
              </CalendlyLink>
              <TrackedLink
                to="/modelos"
                ctaId={CTA_IDS.CAPACIDADES_MODELOS}
                ctaLocation={CTA_LOCATIONS.HERO}
                className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-[#11b36d] font-medium text-base transition-colors duration-200"
              >
                Ver modelos de trabajo
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </TrackedLink>
            </div>
          </div>
        </section>

        {/* Servicios Grid */}
        <section className="py-16 px-4 md:px-6 bg-white dark:bg-[#0d0d0d]">
          <div className="max-w-6xl mx-auto">
            <div ref={gridHeadRef} className="text-center mb-12">
              <div className="flex justify-center">
                <p style={gridHeadStyle(0)} className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Qué construimos</p>
              </div>
              <h2 style={gridHeadStyle(1)} className="text-3xl font-thin tracking-tight sm:text-4xl text-[#0b2b40] dark:text-white">
                Nuestras <span className="text-gradient text-short">capacidades</span>
              </h2>
            </div>

            <div ref={servicesRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {servicios.map((s, i) => (
                <div
                  key={s.id}
                  style={itemStyle(i)}
                  className={`border rounded-2xl p-6 transition-colors duration-300 flex flex-col relative overflow-hidden ${
                    s.highlight
                      ? 'bg-[#f4fdf8] dark:bg-[#0b1a10] border-[#11b36d]/40 dark:border-[#11b36d]/35 hover:border-[#11b36d]/70 shadow-[0_0_28px_rgba(17,179,109,0.07)] dark:shadow-[0_0_32px_rgba(17,179,109,0.13)]'
                      : 'bg-gray-50 dark:bg-[#111111] border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/40'
                  }`}
                >
                  {s.highlight && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#11b36d]/10 text-[#11b36d] border border-[#11b36d]/25">Nuevo</span>
                    </div>
                  )}
                  <div className={`inline-flex items-center justify-center p-2.5 rounded-lg mb-4 w-fit ${
                    s.highlight
                      ? 'bg-[#11b36d]/15 border border-[#11b36d]/30 text-[#11b36d]'
                      : 'bg-[#11b36d]/10 border border-[#11b36d]/20 text-[#11b36d]'
                  }`}>
                    {s.icon}
                  </div>
                  <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{s.subtitle}</p>
                  <ul className="space-y-2 flex-grow">
                    {s.items.map((item, i) => (
                      <li key={i} className="text-slate-500 dark:text-slate-400 text-sm flex items-start gap-2">
                        <span className="text-[#11b36d] mt-0.5 shrink-0">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  {s.link && (
                    <TrackedLink
                      to={s.link}
                      ctaId={CTA_IDS.CAPACIDADES_IA}
                      ctaLocation={CTA_LOCATIONS.PAGE_CTA}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#11b36d] hover:underline transition-colors"
                    >
                      {s.linkText}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </TrackedLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proceso */}
        <section className="py-16 px-4 md:px-6 bg-gray-50 dark:bg-[#0a0a0a]">
          <div className="max-w-4xl mx-auto">
            <div ref={processHeadRef} className="text-center mb-12">
              <div className="flex justify-center">
                <p style={processHeadStyle(0)} className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Metodología</p>
              </div>
              <h2 style={processHeadStyle(1)} className="text-3xl font-thin tracking-tight sm:text-4xl text-[#0b2b40] dark:text-white">
                Cómo <span className="text-gradient text-short">trabajamos</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { n: '01', title: 'Análisis', desc: 'Entendemos tu negocio, el problema y las métricas de éxito.' },
                { n: '02', title: 'Diseño', desc: 'Prototipos, arquitectura y roadmap priorizado por fases.' },
                { n: '03', title: 'Desarrollo', desc: 'Sprints con entregas incrementales y feedback continuo.' },
                { n: '04', title: 'Evolución', desc: 'Deploy, soporte y mejoras. Tu producto crece con vos.' },
              ].map((step) => (
                <div key={step.n} className="bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-2xl p-6">
                  <p className="text-[#11b36d] font-mono text-sm font-semibold mb-3">{step.n}</p>
                  <h3 className="font-medium text-[#0b2b40] dark:text-white mb-2">{step.title}</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="py-16 px-4 md:px-6 bg-white dark:bg-[#0d0d0d]">
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-50 dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-3xl p-10 text-center">
              <div className="flex justify-center">
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Sin compromiso</p>
              </div>
              <h3 className="text-2xl md:text-3xl font-thin tracking-tight text-[#0b2b40] dark:text-white mb-4">
                ¿Listo para empezar?
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-light mb-8">
                Contanos tu idea y te armamos roadmap, costo y plan de pagos en 48 h.
              </p>
              <CalendlyLink
                ctaId={CTA_IDS.CAPACIDADES_FOOTER_CALENDLY}
                ctaLocation={CTA_LOCATIONS.PAGE_CTA}
                className="inline-flex items-center gap-2 bg-[#11b36d] text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
              >
                Armar mi plan
              </CalendlyLink>
            </div>
          </div>
        </section>

        <section className="mt-8" id="footer" ref={footerRef}>
          <Footer />
        </section>
      </div>
    </>
  );
};

export default Services;
